import React, {useCallback, useEffect, useState} from 'react';
import {
    Alert, Box, Button, Checkbox, Chip, CircularProgress, Container, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControlLabel, Paper, Stack, Tab, Tabs, TextField, Typography,
} from '@mui/material';
import {Link as RouterLink} from 'react-router-dom';
import ApiService from '../network/API';
import {STATUS_COLOR, STATUS_LABEL} from './AppealsPage';

const RECHECK_TYPES = ['SSH_LAB', 'MYSQL_DB', 'GIT_REPO'];
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString('ru-RU', {day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit'}) : '');

// Обращения студентов (преподаватель): принять или отклонить с комментарием.
// При принятии по лабе, MySQL- или git-задаче можно сразу перепроверить работу по критериям.
const AdminAppealsPage = () => {
    const [tab, setTab] = useState('OPEN');
    const [items, setItems] = useState(null);
    const [error, setError] = useState(null);
    const [target, setTarget] = useState(null); // {appeal, accept}
    const [comment, setComment] = useState('');
    const [recheck, setRecheck] = useState(true);
    const [busy, setBusy] = useState(false);
    const [notice, setNotice] = useState(null);

    const load = useCallback(() => {
        setItems(null);
        ApiService.getAppeals(tab === 'ALL' ? null : tab).then(setItems).catch((e) => setError(e.reason || e.message || 'Не удалось загрузить'));
    }, [tab]);
    useEffect(load, [load]);

    const open = (appeal, accept) => {
        setTarget({appeal, accept});
        setComment('');
        setRecheck(true);
    };

    const submit = async () => {
        setBusy(true);
        try {
            const result = await ApiService.resolveAppeal(target.appeal.id, target.accept, comment, target.accept && recheck);
            setNotice(result.recheck ? `Обращение обработано. Перепроверка работы: ${result.recheck}` : 'Обращение обработано, студент уведомлён.');
            setTarget(null);
            load();
        } catch (e) {
            setError(e.reason || e.message || 'Не удалось обработать обращение');
        } finally {
            setBusy(false);
        }
    };

    const canRecheck = target && target.accept && RECHECK_TYPES.includes(target.appeal.problemType);

    return (
        <Container maxWidth="md" sx={{py: 4}}>
            <Typography variant="h5" fontWeight={700} sx={{mb: 2}}>Обращения студентов</Typography>
            <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{mb: 2}}>
                <Tab value="OPEN" label="Ожидают"/>
                <Tab value="ACCEPTED" label="Приняты"/>
                <Tab value="REJECTED" label="Отклонены"/>
                <Tab value="ALL" label="Все"/>
            </Tabs>
            {notice && <Alert severity="success" sx={{mb: 2}} onClose={() => setNotice(null)}>{notice}</Alert>}
            {error && <Alert severity="error" sx={{mb: 2}} onClose={() => setError(null)}>{error}</Alert>}
            {!items && <Box textAlign="center" py={6}><CircularProgress size={28}/></Box>}
            {items && items.length === 0 && <Typography color="text.secondary">Здесь пока пусто.</Typography>}
            <Stack spacing={2}>
                {(items || []).map((a) => (
                    <Paper key={a.id} variant="outlined" sx={{p: 2, borderRadius: 2}}>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1} flexWrap="wrap">
                            <Box>
                                <Typography fontWeight={700}>{a.studentName}{a.groupName && <Typography component="span" color="text.secondary"> · {a.groupName}</Typography>}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {a.problemId
                                        ? <RouterLink to={`/problems/${a.problemId}`}>{a.problemTitle}</RouterLink>
                                        : 'Общее обращение'}
                                    {a.articleTitle && ` · ${a.articleTitle}`} · {fmtDate(a.createdAt)}
                                </Typography>
                            </Box>
                            <Chip size="small" color={STATUS_COLOR[a.status]} label={STATUS_LABEL[a.status]}/>
                        </Stack>
                        <Typography sx={{mt: 1.5, whiteSpace: 'pre-wrap'}}>{a.text}</Typography>
                        {a.status !== 'OPEN' && (
                            <Typography variant="body2" color="text.secondary" sx={{mt: 1.5}}>
                                {a.resolvedByName ? `${a.resolvedByName}, ` : ''}{fmtDate(a.resolvedAt)}{a.adminComment ? `: ${a.adminComment}` : ''}
                            </Typography>
                        )}
                        <Stack direction="row" spacing={1} sx={{mt: 1.5}} flexWrap="wrap" useFlexGap>
                            {a.status === 'OPEN' && (
                                <>
                                    <Button size="small" variant="contained" color="success" onClick={() => open(a, true)}>Принять</Button>
                                    <Button size="small" variant="outlined" color="error" onClick={() => open(a, false)}>Отклонить</Button>
                                </>
                            )}
                            {a.problemId && RECHECK_TYPES.includes(a.problemType) && (
                                <Button size="small" component={RouterLink} to={`/admin/problems/${a.problemId}/grading`}>Проверка работ</Button>
                            )}
                        </Stack>
                    </Paper>
                ))}
            </Stack>

            <Dialog open={!!target} onClose={() => setTarget(null)} fullWidth maxWidth="sm">
                <DialogTitle>{target && (target.accept ? 'Принять обращение' : 'Отклонить обращение')}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus multiline minRows={3} fullWidth sx={{mt: 1}}
                        label={target && target.accept ? 'Комментарий студенту (необязательно)' : 'Причина отказа (увидит студент)'}
                        value={comment} onChange={(e) => setComment(e.target.value)}
                    />
                    {canRecheck && (
                        <FormControlLabel sx={{mt: 1}} control={<Checkbox checked={recheck} onChange={(e) => setRecheck(e.target.checked)}/>}
                                          label="Сразу перепроверить работу по критериям (AI)"/>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setTarget(null)}>Отмена</Button>
                    <Button variant="contained" onClick={submit} disabled={busy || (target && !target.accept && !comment.trim())}>
                        {target && target.accept ? 'Принять' : 'Отклонить'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default AdminAppealsPage;
